import type { Clock, RandomSource } from '../clock.js';

const HEX = '0123456789abcdef';

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i] ?? 0;
    out += HEX[(b >> 4) & 0x0f];
    out += HEX[b & 0x0f];
    if (i === 3 || i === 5 || i === 7 || i === 9) out += '-';
  }
  return out;
}

/**
 * UUIDv7: 48 bits de timestamp en milisegundos + version/variante + 74 bits
 * aleatorios. Ordenable por tiempo y unico entre dispositivos, que es lo que
 * necesita la sincronizacion (docs/SYNC_ENGINE.md §2).
 */
export function uuidv7(clock: Clock, random: RandomSource): string {
  const ms = Math.floor(clock.now());
  if (ms < 0 || ms > 0xffff_ffff_ffff) {
    throw new Error(`Timestamp fuera de rango para UUIDv7: ${ms}`);
  }

  const bytes = new Uint8Array(16);
  // 48 bits de timestamp, big-endian.
  bytes[0] = Math.floor(ms / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(ms / 2 ** 32) & 0xff;
  bytes[2] = Math.floor(ms / 2 ** 24) & 0xff;
  bytes[3] = Math.floor(ms / 2 ** 16) & 0xff;
  bytes[4] = Math.floor(ms / 2 ** 8) & 0xff;
  bytes[5] = ms & 0xff;

  for (let i = 6; i < 16; i += 1) {
    bytes[i] = Math.floor(random.next() * 256) & 0xff;
  }

  // Version 7 en el nibble alto del byte 6.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  // Variante RFC 4122 (10xx) en los dos bits altos del byte 8.
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  return toHex(bytes);
}

/** Extrae el instante codificado en un UUIDv7 (util para depurar y ordenar). */
export function timestampFromUuidv7(uuid: string): number {
  const hex = uuid.replace(/-/g, '').slice(0, 12);
  if (hex.length !== 12) throw new Error(`UUID invalido: ${uuid}`);
  return Number.parseInt(hex, 16);
}
